import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { components } from 'react-select';
import { Select, Creatable, colors, useTheme } from '@iq/react-components';

import PopoutSelect from '../PopoutSelect';
import { requestTags, getTags, createTag } from '../../bundles/tags';
import { getActiveSite } from '../../bundles/sites';
import { checkIsOnline, displayNotification, getNotification } from '../../bundles/notifications';
import Loader from '../Loader';
import Tooltip from '../Tooltip/Tooltip';
import LabelWithTooltip from '../LabelWithTooltip';

const MultiValueRemove = (properties) => {
  const {
    limitTags: { allIncludes },
  } = properties.selectProps;
  if (allIncludes && allIncludes.map((tag) => tag.id).includes(properties.data.value)) {
    return null;
  }

  return <components.MultiValueRemove {...properties} />;
};

const MultiValueLabel = (properties) => {
  const { label } = properties.data;

  return (
    <LabelWithTooltip name={label}>
      <components.MultiValueLabel {...properties}>{properties.children}</components.MultiValueLabel>
    </LabelWithTooltip>
  );
};

const ClearIndicator = (properties) => {
  const {
    limitTags: { allIncludes },
  } = properties.selectProps;
  if (allIncludes && allIncludes.length === (properties.getValue() || []).length) {
    return null;
  }
  const clearValue = (event) => {
    event.stopPropagation();
    event.preventDefault();
    let value;
    if (allIncludes) {
      value = properties.selectProps.options.filter((o) =>
        allIncludes.map((tag) => tag.id).includes(o.value)
      );
    }
    properties.setValue(value);
  };

  return (
    <components.ClearIndicator
      {...{
        ...properties,
        innerProps: {
          ...properties.innerProps,
          onMouseDown: clearValue,
          onTouchEnd: clearValue,
        },
      }}
    />
  );
};

const CustomValueContainer = ({ children, ...props }) => {
  const { selectProps, hasValue } = props;
  const { placeholder, inputValue } = selectProps;

  return (
    <components.ValueContainer {...props}>
      {children}
      {hasValue && !inputValue && (
        <div
          className="select-placeholder"
          style={{ opacity: 0.5, margin: 8 }}
        >
          {placeholder}
        </div>
      )}
    </components.ValueContainer>
  );
};

const getOptionsMessage = ({ inputValue }) => {
  const trimmedInput = inputValue?.trim();
  if (!trimmedInput) {
    return 'Please enter a tag name';
  }
  return trimmedInput.length >= 50 ? 'Tag name must not exceed 50 characters' : 'No options found';
};

const formatCreateLabel = (input) => {
  const trimmedInput = input?.trim();
  if (!trimmedInput) {
    return 'Enter a tag name';
  }
  return `Create new tag: "${trimmedInput}"`;
};

const sessionCreatedTagIds = new Set();

export default function TagSelect({
  label = 'Tags',
  icon,
  onChange = () => null,
  value: selectedIds,
  creatable = true,
  popout = false,
  placeholder,
  availableTags,
  category = 'files',
  limitTags = {},
  isDisabled = false,
  menuXPlacement = 'right',
  menuYPlacement = 'auto',
  menuMaxHeight,
  menuMaxWidth,
  menuMinWidth,
  fullHeight = false,
  hideMenu = false,
  editable = true,
  tagLimit = 0,
  ...properties
}) {
  let SelectComponent = Select;
  if (popout) {
    SelectComponent = PopoutSelect;
  } else if (creatable) {
    SelectComponent = Creatable;
  }

  const ref = useRef(null);
  const dispatch = useDispatch();
  const theme = useTheme();
  const site = useSelector(getActiveSite);
  const tags = useSelector(getTags);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!availableTags) {
      dispatch(requestTags(site.id));
    }
  }, [availableTags]);

  const overrideStyles = () => ({
    option: (base, state) => ({
      ...base,
      color: state.isSelected ? colors.Grey10 : base.color,
    }),
    menu: (base) => ({
      ...base,
      ...(fullHeight ? {} : { minHeight: 'unset' }),
      ...(hideMenu ? { display: 'none' } : {}),
      border: `1px solid ${theme === 'dark' ? colors.Grey70 : colors.Grey20}`,
      marginTop: '0.2rem',
      zIndex: '1000',
      ...(menuMaxWidth ? { maxWidth: `${menuMaxWidth}rem` } : {}),
      ...(menuMinWidth ? { minWidth: `${menuMinWidth}rem` } : {}),
    }),
    menuList: (base) => ({
      ...base,
      ...(menuMaxHeight ? { maxHeight: menuMaxHeight } : {}),
      scrollbarColor: `${
        theme === 'dark'
          ? 'rgba(255, 255, 255, .25) rgba(0, 0, 0, 0)'
          : 'rgba(0, 0, 0, .25) rgba(0, 0, 0, 0)'
      }`,
      '::-webkit-scrollbar': {
        width: '7px',
      },
      '::-webkit-scrollbar-thumb': {
        background: `${theme === 'dark' ? 'rgba(255, 255, 255, .25)' : 'rgba(0, 0, 0, .25)'}`,
        borderRadius: '7px',
        boxShadow: `${
          theme === 'dark' ? '0 0 1px rgba(0, 0, 0, .5)' : '"0 0 1px rgba(255, 255, 255, .5)"'
        }`,
      },
    }),
    control: (base) => ({
      ...base,
      backgroundColor: `${theme === 'dark' ? colors.Grey90 : colors.Grey0}`,
      ...(menuMaxWidth ? { maxWidth: `${menuMaxWidth}rem` } : {}),
      ...(menuMinWidth ? { minWidth: `${menuMinWidth}rem` } : {}),
    }),
    multiValue: (base) => ({
      ...base,
      padding: '3px 6px',
      fontSize: '0.875rem',
    }),
  });

  const tagOptions = useMemo(
    () =>
      [
        ...(availableTags || tags).filter((t) => t.categories?.includes(category)),
        ...(availableTags ? tags : []).filter(
          (t) => t.categories?.includes(category) && (selectedIds || []).includes(t.id)
        ),
        ...(availableTags ? tags : []).filter(
          (t) => t.categories?.includes(category) && sessionCreatedTagIds.has(t.id)
        ),
      ]
        // deduplicate
        .filter((t, index, array) => array.findIndex((x) => x.id === t.id) === index)
        .map((t) => ({ value: t.id, label: t.name })),
    [tags, availableTags, category, selectedIds]
  );

  const createTags = useCallback(
    async (name) => {
      const payload = {
        name,
        org: site.org,
        site: site.id,
        categories: [category],
      };
      dispatch(
        createTag(payload, (id) => {
          if (id) {
            sessionCreatedTagIds.add(id);
            onChange(selectedIds ? [...selectedIds, id] : [id]);
          }
        })
      );
    },
    [site, category, createTag, onChange, selectedIds]
  );

  const isExcludedTag = useCallback(
    (name) => {
      if (limitTags.allExcludes) {
        const found = limitTags.allExcludes.find(
          (tag) => tag.name.toLowerCase() === name.toLowerCase()
        );
        if (found) {
          return true;
        }
      }
      return false;
    },
    [limitTags]
  );

  const handleCreate = useCallback(
    async (name) => {
      setIsLoading(true);
      try {
        if (isExcludedTag(name)) {
          dispatch(displayNotification(getNotification('createTag', 'excluded')(name)));
        } else {
          await createTags(name);
        }
      } catch (error) {
        console.error('Unable to create tag:', error);
        dispatch(checkIsOnline());
        dispatch(displayNotification(getNotification('createTag', 'error')(name)));
      }
      setIsLoading(false);
      ref.current.focus();
    },
    [createTags, isExcludedTag, ref.current]
  );

  const handleOnChange = useCallback(
    (selection) => {
      onChange(selection ? selection.map((option) => option.value) : []);
    },
    [onChange]
  );

  const isValidNewOption = useCallback(
    (inputValue) => {
      const trimmedInput = inputValue?.trim();
      if (!trimmedInput || trimmedInput.length >= 50) return false;
      return !tags.some((t) => t.name.toLowerCase() === trimmedInput.toLowerCase());
    },
    [tags]
  );

  if (!tagOptions) {
    return <Loader />;
  }

  const localValue =
    selectedIds &&
    tagOptions.filter((option) => (selectedIds || []).find((id) => option.value === id));

  const limitedTags = tagLimit
    ? tagOptions.map((opt) => ({
        ...opt,
        isDisabled:
          Array.isArray(localValue) &&
          localValue.length >= tagLimit &&
          !localValue.some((selected) => selected.value === opt.value),
      }))
    : tagOptions;

  return editable ? (
    <SelectComponent
      data-testid="popout-select"
      label={popout && label}
      icon={popout && icon}
      isMulti
      innerRef={ref}
      onChange={handleOnChange}
      creatable={creatable}
      onCreateOption={creatable && handleCreate}
      value={localValue?.length ? localValue : null}
      isValidNewOption={isValidNewOption}
      noOptionsMessage={getOptionsMessage}
      formatCreateLabel={creatable && formatCreateLabel}
      options={limitedTags}
      isDisabled={isDisabled || isLoading}
      closeMenuOnSelect={false}
      createOptionPosition="first"
      components={{
        MultiValueRemove,
        ClearIndicator,
        MultiValueLabel,
        ValueContainer: CustomValueContainer,
      }}
      placeholder={placeholder || `Search${creatable ? ' or create' : '...'}`}
      limitTags={limitTags}
      menuXPlacement={menuXPlacement}
      menuYPlacement={popout ? 'bottom' : menuYPlacement}
      styles={(menuMaxHeight || !fullHeight) && overrideStyles()}
      {...properties}
    />
  ) : (
    <div className="tag-display-container">
      {localValue?.map((option, index) => (
        <Tooltip
          content={option.label}
          disabled={option.label.length <= 16}
          key={`${option.value}-${index}`}
        >
          <div
            key={`${option.value}-${index}`}
            className="blocked-item"
          >
            {option.label}
          </div>
        </Tooltip>
      ))}
    </div>
  );
}
